import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { GetAllUsersReferalDetails, UserReferralData } from "../../Admin-Api/GetAllUsersReferalDetails";
import Footer from "../Footer/Footer";
import { Search } from "lucide-react";

interface CustomJwtPayload {
    RoleId: string;
}

const ReferralDetails: React.FC = () => {
    const navigate = useNavigate();
    const hasFetched = useRef(false);

    const [referralData, setReferralData] = useState<UserReferralData[]>([]);
    const [referralLoading, setReferralLoading] = useState(false);

    const [totalUsers, setTotalUsers] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20);
    const [search, setSearch] = useState("");
    const totalPages = Math.ceil(totalUsers / pageSize);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("No auth token found. Please log in.");
            navigate("/");
            return;
        }

        try {
            const decodedToken = jwtDecode<CustomJwtPayload>(token);

            if (decodedToken.RoleId !== "1") {
                toast.error("Unauthorized access");
                navigate("/");
            } else {
                fetchReferralDetails(1);
            }
        } catch {
            toast.error("Invalid token. Please log in again.");
            navigate("/");
        }
    }, [navigate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchReferralDetails(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    const fetchReferralDetails = async (page: number) => {
        setReferralLoading(true);

        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("No auth token found");

            const data = await GetAllUsersReferalDetails(
                page,
                pageSize,
                search,
                token
            );
            setReferralData(data.data);
            setTotalUsers(data.totalRecords);
            setPageNumber(page);
        } catch {
            toast.error("An error occurred while fetching referral details.");
        } finally {
            setReferralLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        fetchReferralDetails(page);
    };

    const getVisiblePages = () => {
        const pages: (number | string)[] = [];

        if (totalPages <= 7) {
            return Array.from(
                { length: totalPages },
                (_, i) => i + 1
            );
        }

        pages.push(1);

        if (pageNumber > 3) {
            pages.push("left-dots");
        }

        if (pageNumber > 2) {
            pages.push(pageNumber - 1);
        }

        if (pageNumber !== 1 && pageNumber !== totalPages) {
            pages.push(pageNumber);
        }

        if (pageNumber < totalPages - 1) {
            pages.push(pageNumber + 1);
        }

        if (pageNumber < totalPages - 3) {
            pages.push("right-dots");
        }

        pages.push(totalPages - 1);
        pages.push(totalPages);

        return pages;
    };


    const openUserReferral = (userId: number) => {
        navigate(`/referral-user/${userId}`);
    };

    return (
        <div className="h-screen overflow-auto bg-[#f5f7fb] select-none">

            <div className="flex flex-col min-h-screen">

                {/* Page Header */}
                <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-8 py-4 bg-white shadow-sm">
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                        <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
                        Referral Details
                    </h1>
                </header>

                <main className="flex-1 overflow-auto px-6 py-5">

                    <div className="max-w-7xl mx-auto">

                        {/* Main Card */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

                            {/* Card Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-b border-gray-200">

                                {/* Title + Total */}
                                <div className="flex items-center">
                                    <h4 className="text-[18px] font-semibold text-[#202124]">
                                        Referral Users
                                    </h4>

                                    <span className="ml-5 text-[14px] text-[#70757A]">
                                        {totalUsers} Records
                                    </span>
                                </div>

                                {/* Search */}
                                <div className="relative w-full sm:w-[300px]">

                                    <Search
                                        size={17}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
                                    />

                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search by name, email or phone"
                                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    />

                                </div>

                            </div>


                            {/* Table Card */}
                            <div className="px-5 py-4">

                                {referralLoading ? (
                                    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-sm text-center text-gray-500">
                                        Loading...
                                    </div>
                                ) : (
                                    <>
                                        <div className="hidden md:block overflow-x-auto">
                                            <table className="min-w-[900px] w-full divide-y divide-gray-200">

                                                <thead className="bg-blue-600">
                                                    <tr>

                                                        <th className="px-3 sm:px-6 py-3 text-xs font-medium text-white uppercase text-center whitespace-nowrap">
                                                            User Name
                                                        </th>

                                                        <th className="px-3 sm:px-6 py-3 text-xs font-medium text-white uppercase text-center whitespace-nowrap">
                                                            Email
                                                        </th>


                                                        <th className="px-3 sm:px-6 py-3 text-xs font-medium text-white uppercase text-center whitespace-nowrap">
                                                            Phone Number
                                                        </th>

                                                        <th className="px-3 sm:px-6 py-3 text-xs font-medium text-white uppercase text-center whitespace-nowrap">
                                                            Created Date
                                                        </th>

                                                        <th className="px-3 sm:px-6 py-3 text-xs font-medium text-white uppercase text-center whitespace-nowrap">
                                                            Referral Count
                                                        </th>

                                                    </tr>
                                                </thead>

                                                <tbody className="bg-white divide-y divide-gray-200">

                                                    {referralData.map((user) => (

                                                        <tr
                                                            key={user.id}
                                                            onClick={() => openUserReferral(user.id)}
                                                            className="hover:bg-gray-50 cursor-pointer transition"
                                                        >


                                                            <td className="px-3 sm:px-6 py-4 text-center text-sm font-medium whitespace-nowrap">
                                                                {user.userName}
                                                            </td>

                                                            <td className="px-3 sm:px-6 py-4 text-center text-sm whitespace-nowrap">
                                                                {user.email}
                                                            </td>

                                                            <td className="px-3 sm:px-6 py-4 text-center text-sm whitespace-nowrap">
                                                                {user.phoneNumber}
                                                            </td>

                                                            <td className="px-3 sm:px-6 py-4 text-center text-sm whitespace-nowrap">
                                                                {user.createdDate}
                                                            </td>

                                                            <td className="px-3 sm:px-6 py-4 text-center">
                                                                <span className="px-3 py-1 rounded-md text-sm font-semibold text-indigo-600">
                                                                    {user.referralCount}
                                                                </span>
                                                            </td>

                                                        </tr>

                                                    ))}

                                                </tbody>

                                            </table>
                                        </div>

                                        {/* Mobile View */}
                                        <div className="block md:hidden p-4 space-y-4 bg-gray-50">                                            {referralData.map((user) => (
                                            <div
                                                key={user.id}
                                                onClick={() => openUserReferral(user.id)}
                                                className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition"
                                            >
                                                <div className="flex items-start justify-between gap-3 mb-3">
                                                    <h2 className="text-sm font-bold text-gray-800 break-words">
                                                        {user.userName}
                                                    </h2>

                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-600 whitespace-nowrap">
                                                        {user.referralCount}
                                                    </span>
                                                </div>

                                                <div className="space-y-2 text-sm text-gray-600">
                                                    <p>
                                                        <span className="font-medium text-gray-800">Email:</span>{" "}
                                                        <span className="break-all">{user.email}</span>
                                                    </p>

                                                    <p>
                                                        <span className="font-medium text-gray-800">Phone:</span>{" "}
                                                        {user.phoneNumber}
                                                    </p>

                                                    <p>
                                                        <span className="font-medium text-gray-800">Created Date:</span>{" "}
                                                        {user.createdDate}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>


                        {/* Pagination */}

                        <div className="flex justify-center mt-6 sm:mt-8 px-2">
                            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-sm px-2 py-1">

                                <button
                                    onClick={() => handlePageChange(pageNumber - 1)}
                                    disabled={pageNumber === 1}
                                    className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-40"
                                >
                                    «
                                </button>

                                {getVisiblePages().map((item, index) =>
                                    item === "left-dots" || item === "right-dots" ? (
                                        <span
                                            key={`${item}-${index}`}
                                            className="px-2 text-gray-400"
                                        >
                                            ...
                                        </span>
                                    ) : (
                                        <button
                                            key={item}
                                            onClick={() => handlePageChange(item as number)}
                                            className={`min-w-[40px] h-[40px] rounded-lg text-sm font-medium transition ${pageNumber === item
                                                ? "bg-blue-600 text-white shadow-sm"
                                                : "text-gray-700 hover:bg-gray-100"
                                                }`}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}

                                <button
                                    onClick={() => handlePageChange(pageNumber + 1)}
                                    disabled={pageNumber === totalPages}
                                    className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-40"
                                >
                                    »
                                </button>

                            </div>
                        </div>

                    </div>

                </main>

            </div>

            <Footer />
            <ToastContainer />

        </div>
    );
};

export default ReferralDetails;